import { useState, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Combobox, ComboboxOption, MultiSelectCombobox } from '@/components/ui/combobox'
import { EditableTable, EditableColumn } from '@/components/ui/EditableTable'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertCircle, Save, ArrowLeft, Clock, Car, Users, Upload } from 'lucide-react'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { useAuth } from '@/contexts/AuthContext'
import { DriverMasterService } from '@/services/driverMasterService'
import { VehicleService } from '@/services/vehicleService'
import { CompensationService } from '@/services/compensationService'
import { DriverMaster } from '@/types/driverMaster'
import { Vehicle } from '@/types/vehicle'
import {
  BulkCompensationRow,
  BulkCompensationData,
  CompensationType,
  CompensationApplicationTarget,
  CompensationCategory,
  CompensationTargetType,
  COMPENSATION_CATEGORIES,
  COMPENSATION_APPLICATION_TARGETS
} from '@/types/compensation'
import { getPeriodOptions, formatPeriodDisplay } from '@/utils/periodHelpers'
import { usePeriodSelection } from '@/hooks/usePeriodSelection'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { toast } from 'sonner'
import { EmptyState } from '@/components/common'
import { SpinnerIcon } from '@/components/ui/SpinnerIcon'
import { CompensationFileUploadDialog, CompensationFileUploadData } from '@/components/compensation/CompensationFileUploadDialog'

export function BulkCompensationPage() {
  const navigate = useNavigate()
  const { currentUser } = useAuth()
  const [loading, setLoading] = useState(false)

  // Smart period selection with suggested default
  const periodOptions = getPeriodOptions()
  const periodValues = periodOptions.map(p => p.value)
  const { suggestedPeriod, savePeriodSelection } = usePeriodSelection('bulk-compensation-last-period', periodValues)
  const [selectedPeriod, setSelectedPeriod] = useState('-') // Default to "-" to force selection
  const [targetType, setTargetType] = useState<CompensationTargetType>('driver')

  // Auto-initialize with suggested period when available
  useEffect(() => {
    if (suggestedPeriod && selectedPeriod === '-') {
      setSelectedPeriod(suggestedPeriod)
    }
  }, [suggestedPeriod, selectedPeriod])
  const [driverMasters, setDriverMasters] = useState<DriverMaster[]>([])
  const [vehicles, setVehicles] = useState<Vehicle[]>([])
  const [selectedDriverIds, setSelectedDriverIds] = useState<string[]>([])
  const [selectedVehicleIds, setSelectedVehicleIds] = useState<string[]>([])
  const [tableData, setTableData] = useState<BulkCompensationRow[]>([])
  const [showValidationErrors, setShowValidationErrors] = useState(false)
  const [showFileUploadDialog, setShowFileUploadDialog] = useState(false)

  // Helper functions
  const getConnectedDriver = (vehicleId: string): DriverMaster | null => {
    return driverMasters.find(driver => driver.carId === vehicleId) || null
  }

  const getVehicleDisplayName = (vehicle: Vehicle): string => {
    const connectedDriver = getConnectedDriver(vehicle.id)
    if (connectedDriver) {
      return `${vehicle.carRegNumber} (${connectedDriver.primaryName})`
    }
    return `${vehicle.carRegNumber} (⚠️ Ingen förare)`
  }

  // Load data when period is selected
  const loadData = async () => {
    if (!currentUser || selectedPeriod === '-') return

    try {
      const [masters, vehiclesData] = await Promise.all([
        DriverMasterService.getDrivers(currentUser),
        VehicleService.getVehicles(currentUser)
      ])

      setDriverMasters(masters)
      setVehicles(vehiclesData)

      if (targetType === 'driver') {
        // Select all drivers by default
        const allDriverIds = masters.map(driver => driver.id)
        setSelectedDriverIds(allDriverIds)
        setSelectedVehicleIds([])

        // Initialize table data with all drivers
        const initialData: BulkCompensationRow[] = masters.map(driver => ({
          driverId: driver.id,
          driverName: driver.primaryName || driver.id,
          targetType: 'driver',
          type: undefined,
          applyTo: undefined,
          amount: undefined,
          category: undefined,
          comment: undefined
        }))
        setTableData(initialData)
      } else {
        // Select all vehicles by default
        const allVehicleIds = vehiclesData.map(vehicle => vehicle.id)
        setSelectedVehicleIds(allVehicleIds)
        setSelectedDriverIds([])

        // Initialize table data with all vehicles
        const initialData: BulkCompensationRow[] = vehiclesData.map(vehicle => ({
          vehicleId: vehicle.id,
          vehicleName: getVehicleDisplayName(vehicle),
          targetType: 'vehicle',
          type: undefined,
          applyTo: undefined,
          amount: undefined,
          category: undefined,
          comment: undefined
        }))
        setTableData(initialData)
      }
    } catch (error) {
      console.error('Error loading data:', error)
      toast.error('Det gick inte att ladda data')
    }
  }

  // Load data when period or target type changes
  useEffect(() => {
    if (selectedPeriod !== '-') {
      loadData()
    } else {
      // Clear data when no period selected
      setDriverMasters([])
      setVehicles([])
      setSelectedDriverIds([])
      setSelectedVehicleIds([])
      setTableData([])
    }
  }, [currentUser, selectedPeriod, targetType])

  // Prepare options for selects
  const compensationTypeOptions = [
    { value: 'compensation', label: 'Ersättning' },
    { value: 'deduction', label: 'Avdrag' }
  ]

  const applicationTargetOptions = COMPENSATION_APPLICATION_TARGETS.map(target => ({
    value: target.value,
    label: target.label
  }))

  const categoryOptions = COMPENSATION_CATEGORIES.map(cat => ({
    value: cat.value,
    label: cat.label
  }))

  // Prepare options for multi-select
  const driverOptions = driverMasters.map(driver => ({
    value: driver.id,
    label: driver.primaryName || driver.id
  }))

  const vehicleOptions = vehicles.map(vehicle => ({
    value: vehicle.id,
    label: getVehicleDisplayName(vehicle)
  }))

  // Table columns definition
  const columns: EditableColumn<BulkCompensationRow>[] = [
    {
      key: targetType === 'driver' ? 'driverName' : 'vehicleName',
      header: targetType === 'driver' ? 'Förare' : 'Bil',
      type: 'readonly',
      width: '200px',
      customHeaderControl: targetType === 'driver'
        ? (driverMasters.length > 0 ? (
            <MultiSelectCombobox
              options={driverOptions}
              values={selectedDriverIds}
              onValuesChange={(newIds) => {
                setSelectedDriverIds(newIds)

                // Update tableData to match selected drivers
                setTableData(prev => {
                  // Keep existing rows for still-selected drivers
                  const existingRows = prev.filter(row => row.driverId && newIds.includes(row.driverId))

                  // Find drivers that need new rows
                  const existingDriverIds = existingRows.map(row => row.driverId).filter(Boolean)
                  const newDriverIds = newIds.filter(id => !existingDriverIds.includes(id))

                  // Create new rows for newly selected drivers
                  const newRows = newDriverIds.map(driverId => {
                    const driver = driverMasters.find(d => d.id === driverId)
                    return {
                      driverId,
                      driverName: driver?.primaryName || driverId,
                      targetType: 'driver' as const,
                      type: undefined,
                      applyTo: undefined,
                      amount: undefined,
                      category: undefined,
                      comment: undefined
                    }
                  })

                  return [...existingRows, ...newRows]
                })
              }}
              placeholder="Välj förare..."
              searchPlaceholder="Sök förare..."
              className="w-full min-w-[180px] h-7 text-xs"
            />
          ) : undefined)
        : (vehicles.length > 0 ? (
            <MultiSelectCombobox
              options={vehicleOptions}
              values={selectedVehicleIds}
              onValuesChange={(newIds) => {
                setSelectedVehicleIds(newIds)

                // Update tableData to match selected vehicles
                setTableData(prev => {
                  // Keep existing rows for still-selected vehicles
                  const existingRows = prev.filter(row => row.vehicleId && newIds.includes(row.vehicleId))

                  // Find vehicles that need new rows
                  const existingVehicleIds = existingRows.map(row => row.vehicleId).filter(Boolean)
                  const newVehicleIds = newIds.filter(id => !existingVehicleIds.includes(id))

                  // Create new rows for newly selected vehicles
                  const newRows = newVehicleIds.map(vehicleId => {
                    const vehicle = vehicles.find(v => v.id === vehicleId)
                    return {
                      vehicleId,
                      vehicleName: vehicle ? getVehicleDisplayName(vehicle) : vehicleId,
                      targetType: 'vehicle' as const,
                      type: undefined,
                      applyTo: undefined,
                      amount: undefined,
                      category: undefined,
                      comment: undefined
                    }
                  })

                  return [...existingRows, ...newRows]
                })
              }}
              placeholder="Välj bilar..."
              searchPlaceholder="Sök bilar..."
              className="w-full min-w-[180px] h-7 text-xs"
            />
          ) : undefined)
    },
    {
      key: 'type',
      header: 'Ersättning/Avdrag',
      type: 'select',
      selectOptions: compensationTypeOptions,
      allowBulkSet: true,
      width: '180px',
      placeholder: 'Välj typ...'
    },
    {
      key: 'applyTo',
      header: 'Tillämpning',
      type: 'select',
      selectOptions: applicationTargetOptions,
      allowBulkSet: true,
      width: '200px',
      placeholder: 'Välj tillämpning...'
    },
    {
      key: 'amount',
      header: 'Belopp (SEK)',
      type: 'number',
      allowBulkSet: true,
      width: '150px',
      placeholder: '0.00'
    },
    {
      key: 'category',
      header: 'Kategori',
      type: 'select',
      selectOptions: categoryOptions,
      allowBulkSet: true,
      width: '180px',
      placeholder: 'Välj kategori...'
    },
    {
      key: 'comment',
      header: 'Kommentar',
      type: 'textarea',
      allowBulkSet: true,
      width: '250px',
      placeholder: 'Valfri kommentar...'
    }
  ]

  // Filter table data based on selected items
  const filteredTableData = useMemo(() => {
    if (targetType === 'driver') {
      return tableData.filter(row => row.driverId && selectedDriverIds.includes(row.driverId))
    } else {
      return tableData.filter(row => row.vehicleId && selectedVehicleIds.includes(row.vehicleId))
    }
  }, [tableData, selectedDriverIds, selectedVehicleIds, targetType])

  const handleBulkSetColumn = (columnKey: keyof BulkCompensationRow, value: any) => {
    setTableData(prevData =>
      prevData.map(row => {
        const shouldUpdate = targetType === 'driver'
          ? row.driverId && selectedDriverIds.includes(row.driverId)
          : row.vehicleId && selectedVehicleIds.includes(row.vehicleId)

        return shouldUpdate ? { ...row, [columnKey]: value } : row
      })
    )
  }

  // Handle file upload data
  const handleFileUploadData = (uploadedData: CompensationFileUploadData[]) => {
    // Convert uploaded data to table rows and merge with existing data
    const newRows: BulkCompensationRow[] = uploadedData.map(data => ({
      driverId: data.driverId,
      vehicleId: data.vehicleId,
      driverName: data.driverName,
      vehicleName: data.vehicleName,
      targetType: targetType,
      type: undefined, // User needs to set this
      applyTo: undefined, // User needs to set this
      amount: data.amount,
      category: undefined, // User needs to set this
      comment: data.originalRowCount > 1
        ? `Summerat från ${data.originalRowCount} rader i filen`
        : undefined
    }))

    // Update selected IDs to include uploaded items
    if (targetType === 'driver') {
      const uploadedDriverIds = uploadedData.map(d => d.driverId).filter(Boolean) as string[]
      setSelectedDriverIds(prev => [...new Set([...prev, ...uploadedDriverIds])])
    } else {
      const uploadedVehicleIds = uploadedData.map(d => d.vehicleId).filter(Boolean) as string[]
      setSelectedVehicleIds(prev => [...new Set([...prev, ...uploadedVehicleIds])])
    }

    // Merge with existing table data
    setTableData(prevData => {
      // Remove any existing rows for the same drivers/vehicles
      const existingFiltered = prevData.filter(row => {
        if (targetType === 'driver') {
          return !row.driverId || !uploadedData.some(d => d.driverId === row.driverId)
        } else {
          return !row.vehicleId || !uploadedData.some(d => d.vehicleId === row.vehicleId)
        }
      })

      return [...existingFiltered, ...newRows]
    })

    toast.success(`${uploadedData.length} ${targetType === 'driver' ? 'förare' : 'bilar'} importerades från filen`)
  }

  // Validation
  const validationErrors = useMemo(() => {
    const errors: string[] = []

    if (!selectedPeriod || selectedPeriod === '-') {
      errors.push('Period är obligatorisk')
    }

    // Check each row for completeness and validity
    tableData.forEach((row, index) => {
      const rowNumber = index + 1
      const hasAnyData = row.type || row.applyTo || row.amount || row.category || row.comment

      if (hasAnyData) {
        // Check for vehicle without connected driver
        if (row.targetType === 'vehicle' && row.vehicleId) {
          const connectedDriver = getConnectedDriver(row.vehicleId)
          if (!connectedDriver) {
            errors.push(`Rad ${rowNumber}: Bilen måste vara kopplad till en förare`)
          }
        }

        if (!row.type) {
          errors.push(`Rad ${rowNumber}: Typ är obligatorisk`)
        }
        if (!row.applyTo) {
          errors.push(`Rad ${rowNumber}: Tillämpning är obligatorisk`)
        }
        if (!row.amount || row.amount <= 0) {
          errors.push(`Rad ${rowNumber}: Belopp måste vara ett positivt tal`)
        }
        if (!row.category) {
          errors.push(`Rad ${rowNumber}: Kategori är obligatorisk`)
        }
      }
    })

    return errors
  }, [tableData, selectedPeriod])

  const completedRows = useMemo(() => {
    return tableData.filter(row => {
      const hasBasicData = row.type && row.applyTo && row.amount && row.amount > 0 && row.category

      // For vehicles, also check that they have a connected driver
      if (row.targetType === 'vehicle' && row.vehicleId) {
        return hasBasicData && getConnectedDriver(row.vehicleId) !== null
      }

      return hasBasicData
    })
  }, [tableData, driverMasters])

  const handleSubmit = async () => {
    setShowValidationErrors(true)

    if (validationErrors.length > 0 || !currentUser || selectedPeriod === '-') return

    setLoading(true)
    try {
      const bulkData: BulkCompensationData = {
        period: selectedPeriod,
        compensations: completedRows.map(row => ({
          driverId: row.driverId,
          vehicleId: row.vehicleId,
          type: row.type!,
          applyTo: row.applyTo!,
          amount: row.amount!,
          category: row.category!,
          comment: row.comment || undefined
        }))
      }

      await CompensationService.createBulkCompensations(currentUser, bulkData)

      toast.success(`${completedRows.length} ersättningar/avdrag skapades framgångsrikt`)

      // Navigate back to salary report
      navigate('/salaryreport')

    } catch (error) {
      console.error('Error creating bulk compensations:', error)
      toast.error(error instanceof Error ? error.message : 'Det gick inte att skapa ersättningar/avdrag')
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    navigate('/salaryreport')
  }

  const handlePeriodChange = (period: string) => {
    setSelectedPeriod(period)
    if (period !== '-') {
      savePeriodSelection(period)
    }
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Page Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="outline"
          size="sm"
          onClick={handleCancel}
          className="gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Tillbaka
        </Button>
        <div>
          <h1 className="text-2xl font-bold">
            Lägg till ersättning/avdrag för flera {targetType === 'driver' ? 'förare' : 'bilar'}
          </h1>
          <p className="text-muted-foreground">
            Fyll i ersättningar/avdrag för flera {targetType === 'driver' ? 'förare' : 'bilar'} samtidigt
          </p>
        </div>
      </div>

      {/* Period and Target Type Selection */}
      <div className="bg-white rounded-lg border p-6">
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Period *</Label>
              <Select value={selectedPeriod} onValueChange={handlePeriodChange}>
                <SelectTrigger className="max-w-xs">
                  <SelectValue placeholder="Välj period..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="-">- Välj period -</SelectItem>
                  {periodOptions.map(period => (
                    <SelectItem key={period.value} value={period.value}>
                      {formatPeriodDisplay(period.value)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-3">
              <Label>Lägg till för *</Label>
              <RadioGroup
                value={targetType}
                onValueChange={(value: CompensationTargetType) => {
                  setTargetType(value)
                  // Clear selections when switching types
                  setSelectedDriverIds([])
                  setSelectedVehicleIds([])
                  setTableData([])
                }}
                className="flex gap-6"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="driver" id="driver-bulk" />
                  <Label htmlFor="driver-bulk" className="font-normal cursor-pointer flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Förare
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="vehicle" id="vehicle-bulk" />
                  <Label htmlFor="vehicle-bulk" className="font-normal cursor-pointer flex items-center gap-2">
                    <Car className="h-4 w-4" />
                    Bilar
                  </Label>
                </div>
              </RadioGroup>
            </div>
          </div>


          {/* Instructions */}
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>Instruktioner:</strong>
              <ul className="mt-2 ml-4 list-disc space-y-1 text-sm">
                <li>Välj först period och sedan {targetType === 'driver' ? 'förare' : 'bilar'} att inkludera</li>
                <li>Använd knapparna ovanför varje kolumn för att sätta samma värde för alla valda {targetType === 'driver' ? 'förare' : 'bilar'}</li>
                <li>Lämna rader tomma för {targetType === 'driver' ? 'förare' : 'bilar'} som inte ska få ersättning/avdrag</li>
                <li>Endast rader med komplett information kommer att sparas</li>
                {targetType === 'vehicle' && (
                  <li className="text-yellow-600 font-medium">⚠️ Bilar måste vara kopplade till förare för att kunna få ersättningar/avdrag</li>
                )}
              </ul>
            </AlertDescription>
          </Alert>
        </div>
      </div>

      {/* Editable Table */}
      <div className="bg-white rounded-lg border p-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">
              Ersättningar/avdrag per {targetType === 'driver' ? 'förare' : 'bil'}
            </h2>
            {selectedPeriod !== '-' && (targetType === 'driver' ? driverMasters.length > 0 : vehicles.length > 0) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFileUploadDialog(true)}
                className="gap-2"
              >
                <Upload className="h-4 w-4" />
                Ladda upp fil
              </Button>
            )}
          </div>

          {selectedPeriod === '-' ? (
            <EmptyState
              message={`Välj en period för att visa ${targetType === 'driver' ? 'förare' : 'bilar'}`}
              description={`Du måste först välja en period för att se tillgängliga ${targetType === 'driver' ? 'förare' : 'bilar'}.`}
            />
          ) : (targetType === 'driver' ? driverMasters.length === 0 : vehicles.length === 0) ? (
            <EmptyState
              icon={<SpinnerIcon size="lg" />}
              message={`Laddar ${targetType === 'driver' ? 'förare' : 'bilar'}...`}
              description={`Hämtar ${targetType === 'driver' ? 'förarlista' : 'billista'} för vald period.`}
            />
          ) : (
            <EditableTable
              data={filteredTableData}
              columns={columns}
              onDataChange={setTableData}
              onBulkSetColumn={handleBulkSetColumn}
              emptyMessage={
                (targetType === 'driver' ? selectedDriverIds.length === 0 : selectedVehicleIds.length === 0)
                  ? `Välj ${targetType === 'driver' ? 'förare' : 'bilar'} att inkludera i tabellen`
                  : `Inga ${targetType === 'driver' ? 'förare' : 'bilar'} tillgängliga`
              }
            />
          )}
        </div>
      </div>

      {/* Summary and Validation */}
      <div className="bg-white rounded-lg border p-6">
        <div className="space-y-4">
          {completedRows.length > 0 && (
            <Alert>
              <AlertDescription>
                <strong>Sammanfattning:</strong> {completedRows.length} ersättningar/avdrag kommer att skapas för perioden {selectedPeriod === '-' ? '(välj period)' : selectedPeriod}.
              </AlertDescription>
            </Alert>
          )}

          {showValidationErrors && validationErrors.length > 0 && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>Fel som måste åtgärdas:</strong>
                <ul className="mt-2 ml-4 list-disc">
                  {validationErrors.slice(0, 10).map((error, index) => (
                    <li key={index} className="text-sm">{error}</li>
                  ))}
                  {validationErrors.length > 10 && (
                    <li className="text-sm">... och {validationErrors.length - 10} fler</li>
                  )}
                </ul>
              </AlertDescription>
            </Alert>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-3 pt-4 border-t bg-white rounded-lg p-6">
        <Button
          variant="outline"
          onClick={handleCancel}
          disabled={loading}
          className="h-10 px-6"
        >
          Avbryt
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading || (showValidationErrors && validationErrors.length > 0) || completedRows.length === 0}
          className="h-10 px-6"
        >
          {loading ? (
            <div className="flex items-center gap-2">
              <SpinnerIcon size="sm" />
              Skapar...
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Save className="h-4 w-4" />
              Skapa {completedRows.length} ersättningar/avdrag
            </div>
          )}
        </Button>
      </div>

      {/* File Upload Dialog */}
      <CompensationFileUploadDialog
        open={showFileUploadDialog}
        onOpenChange={setShowFileUploadDialog}
        onDataUploaded={handleFileUploadData}
        targetType={targetType}
        drivers={driverMasters}
        vehicles={vehicles}
      />
    </div>
  )
}